// src/pages/ia-paca/metiers/developpeur-llm.tsx
//
// Page métier — Développeur LLM en PACA.

import MetierPageTemplate, {
  type MetierPageConfig,
} from '../../../components/ia-paca/MetierPageTemplate';
import { Brain, Cpu, Code2, GitBranch, Activity, Layers } from 'lucide-react';

const CONFIG: MetierPageConfig = {
  slug: 'developpeur-llm',
  metaTitle: 'Recrutement Développeur LLM en PACA — Marseille, Sophia | recruteurs.io',
  metaDescription:
    "Recruter un Développeur LLM en PACA : fine-tuning, serving (vLLM, TGI), quantization, inference GPU optimisée. Profil pointu pour scale-ups et entreprises souveraines. Salaire 55-110k€.",
  metaKeywords:
    'recrutement Développeur LLM, Développeur LLM Marseille, LLM Engineer Sophia, salaire Développeur LLM 2026, recruter expert LLM PACA, fine-tuning LLM',
  heroLabel: 'Développeur LLM',
  heroSubtitle:
    "Le profil tech profond qui sait fine-tuner un modèle, le quantizer, le servir à 1000 req/s avec vLLM, et arbitrer entre Mistral hosted, Llama self-hosted, ou OpenAI API. Indispensable pour les boîtes qui dépassent le simple API consumer.",
  heroBadges: ['Fine-tuning', 'Quantization', 'vLLM / TGI', 'GPU optimization'],
  jsonLdName: 'Développeur LLM',
  jsonLdDescription:
    "Le Développeur LLM travaille au cœur des modèles : fine-tuning (LoRA, QLoRA, full FT), quantization (AWQ, GPTQ), serving optimisé (vLLM, TGI, TensorRT-LLM), évaluation custom, distillation.",
  jsonLdSkills:
    'PyTorch, Transformers, PEFT (LoRA, QLoRA), Axolotl, Unsloth, vLLM, TGI, TensorRT-LLM, quantization (AWQ, GPTQ, GGUF), GPU CUDA basics, distributed training, DeepSpeed, FSDP, Llama / Mistral / Qwen ecosystems',
  jsonLdMinSalary: 55000,
  jsonLdMedianSalary: 80000,
  jsonLdMaxSalary: 110000,
  jsonLdEducation: 'Ingénieur ML, Master IA, ou autodidacte profond avec contributions open-source.',
  jsonLdExperience: '2-10+ ans, junior très rare.',
  intro: {
    title: 'Développeur LLM : quand l\'API OpenAI ne suffit plus.',
    paragraphs: [
      "Pendant que 90% des entreprises consomment des LLM via API, une fraction grandissante a des raisons valables d'aller plus loin : <strong>souveraineté des données, coûts à l'échelle, latence critique, fine-tuning sur domaine spécifique, déploiement on-prem</strong>. C'est là qu'intervient le Développeur LLM.",
      "Concrètement : il choisit le bon modèle de base (Mistral, Llama, Qwen, Mixtral) selon le cas d'usage, prépare le dataset de fine-tuning, lance l'entraînement (LoRA / QLoRA / full FT) avec Axolotl ou Unsloth, évalue les sorties, quantize le modèle (AWQ, GPTQ, GGUF), et le déploie en production avec vLLM, TGI ou TensorRT-LLM en optimisant la batching dynamique et le tokens/sec.",
      "<strong>Pourquoi c'est rare en PACA :</strong> ce profil exige un fond technique pointu (PyTorch, CUDA basics, transformers internals). Beaucoup de Data Scientists s'y autoproclament, peu en ont la pratique. Le marché PACA en compte ~50-100 profils vraiment crédibles.",
    ],
  },
  stackTitle: 'Les outils que votre Développeur LLM doit maîtriser.',
  stack: [
    {
      Icon: Brain,
      title: 'Modèles de base',
      tools: ['Mistral / Mixtral', 'Llama 3 / 4', 'Qwen', 'DeepSeek', 'Phi', 'Gemma'],
    },
    {
      Icon: Layers,
      title: 'Fine-tuning',
      tools: ['Hugging Face Transformers', 'PEFT (LoRA, QLoRA)', 'Axolotl', 'Unsloth', 'TRL', 'DeepSpeed', 'FSDP'],
    },
    {
      Icon: Cpu,
      title: 'Quantization',
      tools: ['AWQ', 'GPTQ', 'GGUF (llama.cpp)', 'bitsandbytes', 'AutoAWQ'],
    },
    {
      Icon: Code2,
      title: 'Serving & inference',
      tools: ['vLLM', 'TGI (Hugging Face)', 'TensorRT-LLM', 'llama.cpp', 'Ollama', 'SGLang'],
    },
    {
      Icon: Activity,
      title: 'Evaluation & benchmarks',
      tools: ['lm-evaluation-harness', 'MT-Bench', 'AlpacaEval', 'datasets custom', 'human eval'],
    },
    {
      Icon: GitBranch,
      title: 'Infra GPU',
      tools: ['H100 / A100 / L40S', 'NVIDIA NCCL', 'CUDA basics', 'multi-GPU training', 'distributed inference'],
    },
  ],
  salaryByCityTitle: '55-110 k€, premium fort sur expérience training distribué + serving prod.',
  salaryByCity: [
    { ville: 'Marseille', junior: '55 – 65 k€', confirme: '65 – 85 k€', senior: '85 – 105 k€', expert: '100 – 115 k€' },
    { ville: 'Aix-en-Provence', junior: '52 – 62 k€', confirme: '62 – 82 k€', senior: '82 – 100 k€', expert: '98 – 112 k€' },
    { ville: 'Sophia Antipolis', junior: '60 – 70 k€', confirme: '70 – 92 k€', senior: '92 – 112 k€', expert: '108 – 125 k€' },
    { ville: 'Nice', junior: '58 – 68 k€', confirme: '68 – 88 k€', senior: '88 – 108 k€', expert: '105 – 120 k€' },
  ],
  questionsTitle: '5 questions pour qualifier un Développeur LLM.',
  questionsIntro:
    "Ces questions séparent un Développeur LLM mature d'un Data Scientist qui a vu passer LoRA dans un blog post.",
  questions: [
    {
      q: 'Tu dois fine-tuner Mistral 7B sur 50 000 paires question/réponse métier. Comment tu structures le training ?',
      good:
        "Décrit : 1) dataset prep (format chat template, déduplication, validation set), 2) choix LoRA vs QLoRA selon GPU (consumer 24 GB → QLoRA), 3) hyperparams initiaux (rank 16-64, alpha 32-128, learning rate 1e-4 à 3e-4), 4) tracking W&B, 5) évaluation (perplexity + benchmark custom + human eval), 6) régularisation contre overfit (early stopping, dropout LoRA). Cite Axolotl ou Unsloth.",
      bad: "'Je lance huggingface trainer avec les paramètres par défaut.' Pas de notion data quality, pas d'éval, overfit garanti.",
    },
    {
      q: 'Tu veux servir Llama 3 70B avec 50 utilisateurs concurrents. Quelle infra et quelle stack ?',
      good:
        "Choix : 2x H100 80GB en tensor parallel via vLLM (ou TensorRT-LLM), quantization AWQ 4-bit si latence acceptable, batching dynamique, prefix caching, monitoring tokens/sec + p50/p95/p99, fallback queue si débordement. Sait estimer le throughput théorique (200-400 tokens/s en batch).",
      bad: "'Je host avec Ollama sur une A10.' Impossible (modèle trop gros), aucune notion serving.",
    },
    {
      q: 'Quelle est la différence entre LoRA et QLoRA, et quand tu choisis quoi ?',
      good:
        "LoRA = adapters low-rank sur les poids du modèle en FP16/BF16. QLoRA = même chose mais avec base model quantizé en 4-bit (NF4) → divise par 4 la VRAM. QLoRA pour limiter VRAM (1 GPU consumer pour 70B), LoRA si VRAM dispo et qualité maximale. Connaît la perte de qualité résiduelle de QLoRA (faible mais existante).",
      bad: "Ne sait pas répondre ou confond les deux.",
    },
    {
      q: 'Pourquoi AWQ plutôt que GPTQ pour la quantization 4-bit en production ?',
      good:
        "AWQ : Activation-aware Weight Quantization. Préserve mieux les poids qui correspondent aux activations importantes → moins de perte de qualité. Bonne intégration vLLM, TGI. GPTQ historiquement plus connu mais souvent battu par AWQ sur benchmarks récents. Sait aussi mentionner GGUF (llama.cpp) pour edge / consumer.",
      bad: "Confond les deux ou ignore les différences techniques.",
    },
    {
      q: 'Tu remarques que ton modèle fine-tuné réussit bien sur le validation set mais hallucine en prod. Hypothèses ?',
      good:
        "Liste : 1) overfit sur le validation set (data leakage), 2) distribution shift production vs training, 3) absence d'instructions / few-shot en prod alors que le training les avait, 4) chat template différent, 5) température inference trop élevée, 6) catastrophic forgetting (le modèle a perdu son comportement de base). Sait débugger méthodiquement.",
      bad: "'On rajoute des exemples au prompt.' Pas de méthodo d'investigation.",
    },
  ],
  ctaTitle: 'Vous cherchez un Développeur LLM en PACA ?',
  ctaSubtitle:
    "Profil rare et chassé par les corporates de Sophia. Notre court-list cible 3-5 candidats avec expérience prod en 25-35 jours.",
};

export default function DeveloppeurLlmPage() {
  return <MetierPageTemplate config={CONFIG} />;
}
